export interface UnlockableItem {
    id: string;
    name: string;
    type: 'css' | 'image';
    value: string; // CSS class name or Image URL
    rarity: 'common' | 'rare';
    locked: boolean; // Default state
    artist?: string;
    year?: string;
    description?: string;
}

export const CARD_BACKS: UnlockableItem[] = [
    { id: 'back_classic_blue', name: 'Classic Blue', type: 'css', value: 'variant-0', rarity: 'common', locked: false },
    { id: 'back_classic_red', name: 'Classic Red', type: 'css', value: 'variant-1', rarity: 'common', locked: false },
    { id: 'back_sunset', name: 'Sunset', type: 'css', value: 'variant-3', rarity: 'common', locked: true },
];

// Auto-load images from src/assets/card_backs
const backImages = import.meta.glob('../assets/card_backs/*.{png,jpg,jpeg,svg}', { eager: true });

for (const path in backImages) {
    const module = backImages[path] as { default: string };
    const fileName = path.split('/').pop()?.split('.')[0] || 'unknown';

    // Parse metadata from filename: Name__Artist__Year.ext
    // Example: Starry_Night__Van_Gogh__1889.jpg
    const parts = fileName.split('__');
    const baseName = parts[0];
    const artist = parts[1] ? parts[1].replace(/_/g, ' ') : undefined;
    const year = parts[2] ? parts[2].replace(/_/g, ' ') : undefined;

    // Convert "some_file_name" to "Some File Name"
    const name = baseName.split(/[_]/).map(word => word.charAt(0) + word.slice(1)).join(' ');

    // ID is based on baseName only, so you can add metadata later without breaking the unlock
    const id = `back_img_${baseName}`;

    CARD_BACKS.push({
        id,
        name,
        type: 'image',
        value: module.default,
        rarity: 'common',
        locked: true,
        artist,
        year
    });
}

export const BOARD_COLORS: UnlockableItem[] = [
    { id: 'board_green', name: 'Classic Green', type: 'css', value: '#2e8b57', rarity: 'common', locked: false },
    { id: 'board_red', name: 'Festive Red', type: 'css', value: '#A1213C', rarity: 'rare', locked: true },
    { id: 'board_midnight', name: 'Midnight Blue', type: 'css', value: '#010126', rarity: 'rare', locked: true },
];

export const CARD_FACES: UnlockableItem[] = [
    { id: 'face_standard', name: 'Standard', type: 'css', value: 'standard', rarity: 'common', locked: false },
    { id: 'face_classic', name: 'Classic', type: 'image', value: 'classic', rarity: 'common', locked: false },
];

export interface GameModeDef {
    id: string;
    name: string;
    icon: string;
    color: string;
    description: string;
    solvable: boolean; // Feasibly solvable by the engine solver
    alwaysWinnable?: boolean; // If true, only one "New Game" button is shown
}

export const GAME_MODES: GameModeDef[] = [
    { id: 'klondike', name: 'Klondike', icon: 'K', color: '#4a90e2', description: 'The classic solitaire game.', solvable: true },
    /*
    { id: 'freecell', name: 'FreeCell', icon: 'F', color: '#50e3c2', description: 'All cards are face up, use four free cells to move cards.', solvable: true, alwaysWinnable: true },
    // { id: 'spider', name: 'Spider', icon: 'S', color: '#b8e986', description: 'Build sequences of the same suit to clear the board.', solvable: false },
    { id: 'spider_1_suit', name: 'Spider (1 Suit)', icon: 'S1', color: '#b8e986', description: 'Spider Solitaire with 1 suit.', solvable: false },
    { id: 'spider_2_suit', name: 'Spider (2 Suits)', icon: 'S2', color: '#b8e986', description: 'Spider Solitaire with 2 suits.', solvable: false },
    { id: 'yukon', name: 'Yukon', icon: 'Y', color: '#f5a623', description: 'Move any face-up group of cards regardless of sequence.', solvable: true },
    { id: 'forty_thieves', name: 'Forty Thieves', icon: '40', color: '#d0021b', description: 'A challenging two-deck game with ten tableau piles.', solvable: false },
    { id: 'scorpion', name: 'Scorpion', icon: 'Sc', color: '#9013fe', description: 'Similar to Yukon but with a different layout and rules.', solvable: true },
    */
    { id: 'golf', name: 'Golf', icon: 'G', color: '#7ed321', description: 'Clear the tableau by moving cards one rank higher or lower.', solvable: false },
    /*
    { id: 'tripeaks', name: 'TriPeaks', icon: 'TP', color: '#417505', description: 'Clear three peaks of cards by matching ranks.', solvable: false },
    { id: 'pyramid', name: 'Pyramid', icon: 'P', color: '#f8e71c', description: 'Pair cards that sum to 13 to clear the pyramid.', solvable: false },
    */
];
